/**
 * /raids — public Raid Hunt board.
 *
 * Two halves: whatever bosses are live right now (`/raids/active`) and the
 * weekly catch leaderboard per server (`/raids/leaderboard`, Mon 00:00 UTC
 * reset). Logged-out visitors see both — the board is a community hook.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Flame, MapPin, Swords, Trophy } from "lucide-react";
import { PageHeader, Loader } from "@/components/Atoms";
import { api, apiError } from "@/lib/api";

const MEDALS = ["text-[#e8c56a]", "text-[#cfd3d8]", "text-[#c08457]"];

export default function Raids() {
  const [board, setBoard] = useState(null);
  const [active, setActive] = useState([]);
  const [streaks, setStreaks] = useState(null);
  const [window_, setWindow] = useState("week");
  const [serverIdx, setServerIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/raids/leaderboard?window=${window_}`),
      api.get("/raids/active"),
      api.get("/raids/streaks"),
    ])
      .then(([lb, act, st]) => {
        if (!alive) return;
        setBoard(lb.data);
        setActive(act.data?.raids || []);
        setStreaks(st.data);
        setError("");
      })
      .catch((e) => alive && setError(apiError(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [window_]);

  const servers = board?.servers || [];
  const current = useMemo(
    () => servers[Math.min(serverIdx, Math.max(servers.length - 1, 0))],
    [servers, serverIdx],
  );

  // player → consecutive weeks with a catch, from the last settled week.
  // Drives the flame badge next to regulars on the board.
  const streakByPlayer = useMemo(() => {
    const m = new Map();
    for (const s of streaks?.streaks || []) {
      const key = `${s.server}|${s.player}`;
      m.set(key, Math.max(m.get(key) || 0, s.streak_weeks || 0));
    }
    return m;
  }, [streaks]);

  return (
    <div className="space-y-6" data-testid="raids-page">
      <PageHeader
        title="Raid Hunt"
        subtitle="Alpha bosses drop across the servers — catch one and land on the board."
      />

      {active.length > 0 && (
        <div className="glass-card p-5" data-testid="raids-active-panel">
          <div className="flex items-center gap-2 mb-3">
            <Swords size={16} className="text-[var(--error)]" />
            <h3 className="text-lg">Live right now</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {active.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/map?raid=${encodeURIComponent(r.id)}`)}
                className="text-left glass-card p-4 hover:border-white/20 transition-colors flex items-center gap-3"
                data-testid={`raids-active-${r.id}`}
              >
                {r.pal_image && (
                  <img src={r.pal_image} alt="" className="w-11 h-11 rounded-full object-cover border border-white/15" loading="lazy" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">Lv {r.level} {r.pal_name}</div>
                  <div className="text-xs text-[var(--muted)] truncate">{r.server} · {r.location_name}</div>
                </div>
                <MapPin size={14} className="ml-auto text-[var(--muted)]" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="glass-card p-5">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <Trophy size={16} className="text-[#e8c56a]" />
          <h3 className="text-lg">Catch leaderboard</h3>
          <div className="ml-auto flex gap-2">
            {[["week", "This week"], ["all", "All time"]].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setWindow(k)}
                className={`btn text-xs ${window_ === k ? "" : "btn-secondary"}`}
                data-testid={`raids-window-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <Loader label="Counting catches…" />
        ) : error ? (
          <p className="text-sm text-[var(--error)]" data-testid="raids-leaderboard-error">{error}</p>
        ) : (
          <>
            <div className="flex gap-2 flex-wrap mb-4">
              {servers.map((s, i) => (
                <button
                  key={s.server}
                  onClick={() => setServerIdx(i)}
                  className={`btn text-xs ${i === serverIdx ? "" : "btn-secondary"}`}
                  data-testid={`raids-server-tab-${i}`}
                >
                  {s.server}
                  <span className="opacity-60 ml-1.5">{s.total_catches}</span>
                </button>
              ))}
            </div>

            {!current || current.rows.length === 0 ? (
              <p className="text-sm text-[var(--muted)] py-6 text-center" data-testid="raids-leaderboard-empty">
                No catches recorded {window_ === "week" ? "this week" : "yet"} on{" "}
                {current?.server || "this server"}. Be the first.
              </p>
            ) : (
              <div className="divide-y divide-white/5" data-testid="raids-leaderboard-rows">
                {current.rows.map((row) => {
                  const streak = streakByPlayer.get(`${current.server}|${row.player}`) || 0;
                  return (
                  <div
                    key={`${current.server}-${row.player}`}
                    className="py-3 flex items-center gap-3"
                    data-testid={`raids-leaderboard-row-${row.rank}`}
                  >
                    <span className={`w-7 text-center font-semibold ${MEDALS[row.rank - 1] || "text-[var(--muted)]"}`}>
                      {row.rank <= 3 ? <Crown size={14} className="inline" /> : row.rank}
                    </span>
                    <span className="flex-1 min-w-0 truncate text-sm">{row.player}</span>
                    {streak >= 2 && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider"
                        style={{ background: "#e07a3f22", border: "1px solid #e07a3f66", color: "#e07a3f" }}
                        title={`${streak} weeks in a row`}
                        data-testid={`raids-streak-badge-${row.rank}`}
                      >
                        <Flame size={10} /> {streak}w
                      </span>
                    )}
                    <span className="text-sm glow-gold">{row.catches}</span>
                    <span className="text-[11px] text-[var(--muted)] hidden sm:inline">
                      {row.last_catch ? new Date(row.last_catch).toLocaleDateString() : ""}
                    </span>
                  </div>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-[var(--muted)] mt-4">
              Weekly board resets Monday 00:00 UTC. Link your Discord with{" "}
              <code>/link</code> so your catches earn rewards too.
            </p>
          </>
        )}
      </div>

      {streaks?.rewards?.enabled && (
        <div className="glass-card p-5" data-testid="raids-streaks-panel">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Flame size={16} className="text-[#e07a3f]" />
            <h3 className="text-lg">Weekly payouts &amp; streaks</h3>
            <span className="ml-auto text-[11px] text-[var(--muted)]" data-testid="raids-reward-tiers">
              {streaks.rewards.top_coins.map((c) => c.toLocaleString()).join(" / ")} coins
              {streaks.rewards.streak_bonus_per_week > 0 &&
                ` · +${streaks.rewards.streak_bonus_per_week.toLocaleString()} per streak week`}
            </span>
          </div>
          <p className="text-xs text-[var(--muted)] mb-4">
            Every Monday the top catchers on each server get paid automatically. Keep
            catching week after week and the streak bonus grows.
          </p>

          {(streaks.last_week?.awards || []).length > 0 ? (
            <div className="divide-y divide-white/5" data-testid="raids-last-week-awards">
              {streaks.last_week.awards.map((a) => (
                <div key={`${a.server}-${a.player}`} className="py-2.5 flex items-center gap-3 text-sm"
                     data-testid={`raids-award-${a.rank}`}>
                  <span className={`w-7 text-center font-semibold ${MEDALS[a.rank - 1] || "text-[var(--muted)]"}`}>
                    {a.rank}
                  </span>
                  <span className="flex-1 min-w-0 truncate">{a.player}</span>
                  <span className="text-[11px] text-[var(--muted)] hidden sm:inline truncate">{a.server}</span>
                  {a.streak_weeks > 1 && (
                    <span className="text-[11px] text-[#e07a3f]">{a.streak_weeks}w streak</span>
                  )}
                  <span className="glow-gold text-sm">+{(a.coins || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--muted)] py-4 text-center" data-testid="raids-awards-empty">
              No payouts yet — the first settlement lands next Monday.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
